// src/components/FinanceView.jsx
// Capital Outlay Forecasting & Deal Financial Overview
import React, { useState, useMemo } from 'react';
import {
  ArrowLeft, LogOut, DollarSign, Calendar, TrendingUp, AlertTriangle,
  ChevronDown, ChevronRight, Bell, Check, Building2, Clock
} from 'lucide-react';
import { useCapitalForecast } from '../hooks/useSupabase';
import { useDisplayColors } from '../lib/ThemeContext';

const REMINDER_TYPE_LABELS = {
  capital_outlay: { label: 'Capital Outlay', color: 'bg-blue-100 text-blue-700' },
  deferred_transaction: { label: 'Deferred Transaction', color: 'bg-purple-100 text-purple-700' },
  earnout: { label: 'Earnout', color: 'bg-indigo-100 text-indigo-700' },
  real_estate: { label: 'Real Estate', color: 'bg-emerald-100 text-emerald-700' },
  seller_note: { label: 'Seller Note', color: 'bg-teal-100 text-teal-700' },
  follow_up: { label: 'Follow-up', color: 'bg-gray-100 text-gray-600' },
  other: { label: 'Other', color: 'bg-gray-100 text-gray-600' },
};

export default function FinanceView({ organizationId, pc, sc, onBack, showToast, signOut, setActiveDealId, setView }) {
  const { displayPrimary, displaySecondary, displayNavy, displayGreen } = useDisplayColors(pc, sc);
  const { forecast, reminders, loading } = useCapitalForecast(organizationId);
  const [timeHorizon, setTimeHorizon] = useState(6); // months
  const [expandedMonths, setExpandedMonths] = useState({});

  // Build monthly forecast from deal close dates + reminders
  const monthlyForecast = useMemo(() => {
    const months = {};
    const now = new Date();
    
    // Initialize months
    for (let i = 0; i < timeHorizon; i++) {
      const d = new Date(now.getFullYear(), now.getMonth() + i, 1);
      const key = d.getFullYear() + '-' + String(d.getMonth() + 1).padStart(2, '0');
      months[key] = {
        key,
        label: d.toLocaleDateString('en-US', { month: 'long', year: 'numeric' }),
        closings: [],
        reminders: [],
        totalClosings: 0,
        totalReminders: 0,
        total: 0,
      };
    }

    // Add deal closings (active deals with target close date and purchase price)
    forecast.forEach(deal => {
      if (!deal.target_close_date || !deal.purchase_price) return;
      if (deal.stage === 'complete') return; // Already closed
      const closeDate = new Date(deal.target_close_date + 'T00:00:00');
      const key = closeDate.getFullYear() + '-' + String(closeDate.getMonth() + 1).padStart(2, '0');
      if (months[key]) {
        months[key].closings.push(deal);
        months[key].totalClosings += Number(deal.purchase_price);
        months[key].total += Number(deal.purchase_price);
      }
    });

    // Add reminders with amounts
    reminders.forEach(rem => {
      if (!rem.due_date) return;
      const remDate = new Date(rem.due_date + 'T00:00:00');
      const key = remDate.getFullYear() + '-' + String(remDate.getMonth() + 1).padStart(2, '0');
      if (months[key]) {
        months[key].reminders.push(rem);
        if (rem.amount) {
          months[key].totalReminders += Number(rem.amount);
          months[key].total += Number(rem.amount);
        }
      }
    });

    return Object.values(months);
  }, [forecast, reminders, timeHorizon]);

  // Summary stats
  const totalPipelineValue = forecast.filter(d => d.purchase_price && d.stage !== 'complete').reduce((s, d) => s + Number(d.purchase_price), 0);
  const totalPendingReminders = reminders.filter(r => r.amount).reduce((s, r) => s + Number(r.amount), 0);
  const overdueForecast = monthlyForecast.length > 0 ? monthlyForecast[0] : null;
  const totalForecastValue = monthlyForecast.reduce((s, m) => s + m.total, 0);

  // Past-due reminders (before current month)
  const overdueReminders = reminders.filter(r => {
    if (!r.due_date) return false;
    return new Date(r.due_date + 'T00:00:00') < new Date();
  });

  const toggleMonth = (key) => {
    setExpandedMonths(prev => ({ ...prev, [key]: !prev[key] }));
  };

  if (loading) {
    return (
      <div className="flex flex-col items-center justify-center min-h-screen">
        <div className="w-8 h-8 border-4 border-gray-200 rounded-full animate-spin mb-4" style={{ borderTopColor: pc }} />
        <p className="text-gray-500 text-sm">Loading financial data...</p>
      </div>
    );
  }

  return (
    <div className="max-w-7xl mx-auto px-6 lg:px-8 pt-8 pb-12">
      {/* Page heading */}
      <div className="mb-6 flex items-start justify-between gap-4 flex-wrap">
        <div>
          <h1
            className="text-2xl font-semibold tracking-tight"
            style={{
              background: 'linear-gradient(135deg, #0F172A 0%, #1A3665 100%)',
              WebkitBackgroundClip: 'text',
              WebkitTextFillColor: 'transparent',
              backgroundClip: 'text',
              letterSpacing: '-0.02em',
            }}
          >
            Capital Forecast
          </h1>
          <p className="text-sm mt-1" style={{ color: '#475569' }}>
            Pipeline value, projected closings, and funding horizon
          </p>
        </div>
        <div className="flex items-center gap-2">
          <span className="text-[10px] font-semibold uppercase tracking-[0.1em]" style={{ color: '#94A3B8' }}>Horizon</span>
          <select
            value={timeHorizon}
            onChange={e => setTimeHorizon(parseInt(e.target.value))}
            className="h-9 text-sm rounded-lg px-3 cursor-pointer focus:outline-none"
            style={{ border: '1px solid #E5E9F0', color: '#0F172A', backgroundColor: 'white' }}
          >
            <option value={3}>3 months</option>
            <option value={6}>6 months</option>
            <option value={12}>12 months</option>
            <option value={18}>18 months</option>
          </select>
        </div>
      </div>

      {/* Summary Cards */}
      <div className="grid grid-cols-2 md:grid-cols-4 gap-3 mb-5">
        <FinStat label="Pipeline Value" value={'$' + totalPipelineValue.toLocaleString()} accent="#1A3665"
          caption={`${forecast.filter(d => d.purchase_price && d.stage !== 'complete').length} active deals`} />
        <FinStat label="Pending Obligations" value={'$' + totalPendingReminders.toLocaleString()} accent="#D97706"
          caption={`${reminders.length} reminder${reminders.length === 1 ? '' : 's'}`} />
        <FinStat label={`${timeHorizon}-Month Forecast`} value={'$' + totalForecastValue.toLocaleString()} accent="#2563EB"
          caption="projected outflow" />
        <FinStat label="Overdue"
          value={overdueReminders.length > 0 ? '$' + overdueReminders.filter(r => r.amount).reduce((s, r) => s + Number(r.amount), 0).toLocaleString() : '$0'}
          accent={overdueReminders.length > 0 ? '#DC2626' : '#94A3B8'}
          caption={`${overdueReminders.length} overdue item${overdueReminders.length === 1 ? '' : 's'}`} />
      </div>

      {/* Overdue Alert */}
      {overdueReminders.length > 0 && (
        <div
          className="mb-5 rounded-xl overflow-hidden"
          style={{
            backgroundColor: '#FEF2F2',
            border: '1px solid #FECACA',
            boxShadow: '0 1px 2px rgba(220, 38, 38, 0.06)',
          }}
        >
          <div className="px-4 py-3 flex items-center gap-2" style={{ borderBottom: '1px solid #FEE2E2' }}>
            <div className="w-7 h-7 rounded-md flex items-center justify-center flex-shrink-0"
                 style={{ backgroundColor: '#FEE2E2', color: '#DC2626' }}>
              <AlertTriangle className="w-3.5 h-3.5" strokeWidth={2.2} />
            </div>
            <div className="text-[11px] font-semibold uppercase tracking-wider" style={{ color: '#991B1B' }}>
              Overdue Items <span className="tabular-nums">· {overdueReminders.length}</span>
            </div>
          </div>
          <div>
            {overdueReminders.map(rem => {
              const dealName = rem.deals ? rem.deals.name : (forecast.find(d => d.id === rem.deal_id)?.name || 'Unknown Deal');
              return (
                <div key={rem.id}
                     className="flex items-center justify-between px-4 py-2.5 transition-colors hover:bg-red-50/50"
                     style={{ borderTop: '1px solid #FEE2E2' }}>
                  <div className="flex items-center gap-2 min-w-0">
                    <span className="text-sm font-semibold" style={{ color: '#991B1B' }}>{rem.title}</span>
                    <span style={{ color: '#FCA5A5' }}>·</span>
                    <span className="text-sm truncate" style={{ color: '#B91C1C' }}>{dealName}</span>
                  </div>
                  <div className="flex items-center gap-3 flex-shrink-0">
                    <span className="text-xs tabular-nums" style={{ color: '#DC2626' }}>{new Date(rem.due_date + 'T00:00:00').toLocaleDateString()}</span>
                    {rem.amount && <span className="text-sm font-semibold tabular-nums" style={{ color: '#991B1B' }}>${Number(rem.amount).toLocaleString()}</span>}
                  </div>
                </div>
              );
            })}
          </div>
        </div>
      )}

      {/* Monthly Forecast */}
      <div className="space-y-3">
        {monthlyForecast.map(month => {
          const isExpanded = expandedMonths[month.key] !== false;
          const hasItems = month.closings.length > 0 || month.reminders.length > 0;
          return (
            <div
              key={month.key}
              className="rounded-2xl overflow-hidden transition-opacity"
              style={{
                backgroundColor: '#ffffff',
                border: '1px solid #E5E9F0',
                boxShadow: '0 1px 2px rgba(15, 23, 42, 0.04), 0 4px 12px rgba(15, 23, 42, 0.03)',
                opacity: month.total > 0 ? 1 : 0.65,
              }}
            >
              <button
                onClick={() => toggleMonth(month.key)}
                className="w-full flex items-center justify-between px-5 py-4 transition-colors hover:bg-[#FAFBFD] text-left"
              >
                <div className="flex items-center gap-3">
                  {isExpanded
                    ? <ChevronDown className="w-4 h-4" style={{ color: '#94A3B8' }} />
                    : <ChevronRight className="w-4 h-4" style={{ color: '#94A3B8' }} />}
                  <span className="text-sm font-semibold" style={{ color: '#0F172A', letterSpacing: '-0.005em' }}>{month.label}</span>
                  {month.closings.length > 0 && (
                    <span className="text-[10px] font-semibold tabular-nums px-1.5 py-0.5 rounded"
                          style={{ backgroundColor: '#DBEAFE', color: '#1E40AF' }}>
                      {month.closings.length} closing{month.closings.length !== 1 ? 's' : ''}
                    </span>
                  )}
                  {month.reminders.length > 0 && (
                    <span className="text-[10px] font-semibold tabular-nums px-1.5 py-0.5 rounded"
                          style={{ backgroundColor: '#FEF3C7', color: '#A16207' }}>
                      {month.reminders.length} obligation{month.reminders.length !== 1 ? 's' : ''}
                    </span>
                  )}
                </div>
                <div className="flex items-center gap-4 text-xs tabular-nums">
                  {month.totalClosings > 0 && (
                    <span style={{ color: '#2563EB' }}>${month.totalClosings.toLocaleString()} closings</span>
                  )}
                  {month.totalReminders > 0 && (
                    <span style={{ color: '#D97706' }}>${month.totalReminders.toLocaleString()} obligations</span>
                  )}
                  <span className="text-sm font-semibold" style={{ color: month.total > 0 ? '#0F172A' : '#94A3B8' }}>
                    ${month.total.toLocaleString()}
                  </span>
                </div>
              </button>

              {isExpanded && hasItems && (
                <div className="px-5 pb-4" style={{ borderTop: '1px solid #F0F2F6' }}>
                  {month.closings.length > 0 && (
                    <div className="pt-3">
                      <div className="text-[10px] font-semibold uppercase tracking-[0.1em] mb-2" style={{ color: '#94A3B8' }}>
                        Acquisition Closings
                      </div>
                      <div className="space-y-1">
                        {month.closings.map(deal => (
                          <div
                            key={deal.id}
                            className="flex items-center justify-between px-3 py-2.5 rounded-lg cursor-pointer transition-colors hover:bg-[#FAFBFD]"
                            onClick={() => { setActiveDealId(deal.id); setView('deal'); }}
                          >
                            <div className="flex items-center gap-3">
                              <div className="w-7 h-7 rounded-md flex items-center justify-center flex-shrink-0"
                                   style={{ backgroundColor: '#DBEAFE', color: '#2563EB' }}>
                                <Building2 className="w-3.5 h-3.5" strokeWidth={2.2} />
                              </div>
                              <div>
                                <div className="text-sm font-semibold" style={{ color: '#0F172A' }}>{deal.name}</div>
                                <div className="text-[11px] flex items-center gap-2 tabular-nums" style={{ color: '#94A3B8' }}>
                                  <span>Close {new Date(deal.target_close_date + 'T00:00:00').toLocaleDateString()}</span>
                                  {deal.ebitda && <><span style={{ color: '#CBD5E1' }}>·</span><span>EBITDA ${Number(deal.ebitda).toLocaleString()}</span></>}
                                  {deal.ebitda && deal.purchase_price && <><span style={{ color: '#CBD5E1' }}>·</span><span>{(deal.purchase_price / deal.ebitda).toFixed(1)}x</span></>}
                                </div>
                              </div>
                            </div>
                            <span className="text-sm font-semibold tabular-nums" style={{ color: '#1E40AF' }}>
                              ${Number(deal.purchase_price).toLocaleString()}
                            </span>
                          </div>
                        ))}
                      </div>
                    </div>
                  )}
                  {month.reminders.length > 0 && (
                    <div className={month.closings.length > 0 ? 'pt-3 mt-3' : 'pt-3'}
                         style={month.closings.length > 0 ? { borderTop: '1px solid #F0F2F6' } : {}}>
                      <div className="text-[10px] font-semibold uppercase tracking-[0.1em] mb-2" style={{ color: '#94A3B8' }}>
                        Obligations & Reminders
                      </div>
                      <div className="space-y-1">
                        {month.reminders.map(rem => {
                          const typeInfo = REMINDER_TYPE_LABELS[rem.reminder_type] || REMINDER_TYPE_LABELS.other;
                          const dealName = rem.deals ? rem.deals.name : (forecast.find(d => d.id === rem.deal_id)?.name || '');
                          return (
                            <div key={rem.id} className="flex items-center justify-between px-3 py-2.5 rounded-lg transition-colors hover:bg-[#FAFBFD]">
                              <div className="flex items-center gap-3 min-w-0">
                                <div className="w-7 h-7 rounded-md flex items-center justify-center flex-shrink-0"
                                     style={{ backgroundColor: '#FEF3C7', color: '#D97706' }}>
                                  <Bell className="w-3.5 h-3.5" strokeWidth={2.2} />
                                </div>
                                <div className="min-w-0">
                                  <div className="flex items-center gap-1.5">
                                    <span className={`text-[10px] font-semibold px-1.5 py-0.5 rounded ${typeInfo.color}`}>{typeInfo.label}</span>
                                    <span className="text-sm font-semibold" style={{ color: '#0F172A' }}>{rem.title}</span>
                                  </div>
                                  <div className="text-[11px] flex items-center gap-2 tabular-nums mt-0.5" style={{ color: '#94A3B8' }}>
                                    {dealName && <span>{dealName}</span>}
                                    {dealName && <span style={{ color: '#CBD5E1' }}>·</span>}
                                    <span>{new Date(rem.due_date + 'T00:00:00').toLocaleDateString()}</span>
                                  </div>
                                  {rem.description && <div className="text-[11px] mt-0.5" style={{ color: '#64748B' }}>{rem.description}</div>}
                                </div>
                              </div>
                              {rem.amount ? (
                                <span className="text-sm font-semibold tabular-nums flex-shrink-0" style={{ color: '#D97706' }}>
                                  ${Number(rem.amount).toLocaleString()}
                                </span>
                              ) : (
                                <span className="text-[11px] flex-shrink-0" style={{ color: '#94A3B8' }}>No amount</span>
                              )}
                            </div>
                          );
                        })}
                      </div>
                    </div>
                  )}
                </div>
              )}

              {isExpanded && !hasItems && (
                <div className="px-5 py-6 text-center text-sm" style={{ borderTop: '1px solid #F0F2F6', color: '#94A3B8' }}>
                  No projected cash outflows this month
                </div>
              )}
            </div>
          );
        })}
      </div>

      {/* Deal Metrics Table */}
      <div className="mt-8">
        <div className="mb-3">
          <h2 className="text-base font-semibold tracking-tight" style={{ color: '#0F172A', letterSpacing: '-0.01em' }}>
            Deal Financial Summary
          </h2>
          <p className="text-xs mt-0.5" style={{ color: '#94A3B8' }}>
            Snapshot of price, EBITDA, and multiples across every deal with financials
          </p>
        </div>
        <div className="rounded-2xl overflow-hidden"
             style={{
               backgroundColor: '#ffffff',
               border: '1px solid #E5E9F0',
               boxShadow: '0 1px 2px rgba(15, 23, 42, 0.04), 0 4px 12px rgba(15, 23, 42, 0.03)',
             }}>
          <div className="grid grid-cols-9 gap-2 px-5 py-3 text-[10px] font-semibold uppercase tracking-[0.1em]"
               style={{ backgroundColor: '#FAFBFD', borderBottom: '1px solid #F0F2F6', color: '#94A3B8' }}>
            <div className="col-span-2">Deal</div>
            <div>Stage</div>
            <div className="text-right">Price</div>
            <div className="text-right">EBITDA</div>
            <div className="text-right">Multiple</div>
            <div className="text-right">Revenue</div>
            <div className="text-right">Calls</div>
            <div className="text-right">Target Close</div>
          </div>
          {forecast.filter(d => d.purchase_price || d.ebitda || d.annual_revenue).length === 0 ? (
            <div className="px-5 py-10 text-center text-sm" style={{ color: '#94A3B8' }}>
              No financial data entered yet. Add financials from individual deal views.
            </div>
          ) : (
            forecast.filter(d => d.purchase_price || d.ebitda || d.annual_revenue).map(deal => (
              <div
                key={deal.id}
                className="grid grid-cols-9 gap-2 px-5 py-3 items-center text-sm cursor-pointer transition-colors hover:bg-[#FAFBFD]"
                style={{ borderTop: '1px solid #F0F2F6' }}
                onClick={() => { setActiveDealId(deal.id); setView('deal'); }}
              >
                <div className="col-span-2 font-semibold truncate" style={{ color: '#0F172A' }}>{deal.name}</div>
                <div className="text-xs" style={{ color: '#64748B' }}>{deal.stage}</div>
                <div className="text-right font-medium tabular-nums" style={{ color: '#0F172A' }}>
                  {deal.purchase_price ? '$' + Number(deal.purchase_price).toLocaleString() : '—'}
                </div>
                <div className="text-right tabular-nums" style={{ color: '#334155' }}>
                  {deal.ebitda ? '$' + Number(deal.ebitda).toLocaleString() : '—'}
                </div>
                <div className="text-right font-semibold tabular-nums" style={{ color: '#1A3665' }}>
                  {deal.purchase_price && deal.ebitda ? (Number(deal.purchase_price) / Number(deal.ebitda)).toFixed(1) + 'x' : '—'}
                </div>
                <div className="text-right tabular-nums" style={{ color: '#334155' }}>
                  {deal.annual_revenue ? '$' + Number(deal.annual_revenue).toLocaleString() : '—'}
                </div>
                <div className="text-right tabular-nums" style={{ color: '#334155' }}>
                  {deal.annual_calls ? Number(deal.annual_calls).toLocaleString() : '—'}
                </div>
                <div className="text-right text-xs tabular-nums" style={{ color: '#64748B' }}>
                  {deal.target_close_date ? new Date(deal.target_close_date + 'T00:00:00').toLocaleDateString() : '—'}
                </div>
              </div>
            ))
          )}
        </div>
      </div>
    </div>
  );
}

// Local stat tile — mirrors the Portfolio / Activity stat pattern
function FinStat({ label, value, accent, caption }) {
  return (
    <div
      className="rounded-2xl px-4 py-3"
      style={{
        background: 'linear-gradient(180deg, #FFFFFF 0%, #FAFBFD 100%)',
        border: '1px solid #E5E9F0',
        boxShadow: '0 1px 2px rgba(15, 23, 42, 0.04), 0 1px 3px rgba(15, 23, 42, 0.02)',
      }}
    >
      <div className="text-[10px] font-semibold uppercase tracking-[0.1em]" style={{ color: '#94A3B8' }}>
        {label}
      </div>
      <div className="text-xl font-semibold mt-1 tabular-nums leading-none" style={{ color: accent, letterSpacing: '-0.015em' }}>
        {value}
      </div>
      {caption && (
        <div className="text-[11px] mt-1.5 tabular-nums" style={{ color: '#94A3B8' }}>
          {caption}
        </div>
      )}
    </div>
  );
}
